<template>
    <div class="c-recipe-product" :class="{'has-alternatives-open': hasAlternativesOpen}">
        <div class="c-recipe-product__content" ref="content">
            <div class="c-recipe-product__amount">
                <div class="c-badge c-badge--ultra-light-grey">
                    {{ amount }}
                </div>
            </div>

            <a
                class="c-recipe-product__image c-link"
                :href="product.link"
                :title="product.name"
                target="_blank"
            >
                <img class="c-img" :src="thumbnail(product.image, 'xs')" :alt="product.name" />
            </a>

            <div class="c-recipe-product__name">
                <a
                    class="sc-name c-link"
                    :href="product.link"
                    :title="product.name"
                    target="_blank"
                >
                    {{ product.name }}
                </a>

                <button
                    class="c-btn sc-alternative-trigger"
                    :class="{'is-active': hasAlternativesOpen}"
                    v-if="alternatives.length"
                    @click="toggleAlternatives"
                >
                    <i class="ico ico-chevron-down"></i>
                </button>
            </div>

            <div class="c-recipe-product__quantity">
                <span>{{ product.quantity }}</span>
            </div>

            <div class="c-recipe-product__availability">
                <span class="js-tippy" :data-tippy-content="product.availability_tooltip">{{ product.availability || 'Na sklade > 100 kg' }}</span>
            </div>

            <div class="c-recipe-product__price">
                <div
                    class="sc-actual"
                    :class="{'has-action': product.is_action}"
                    v-html="formatPrice(product.final_price)"
                ></div>
                <div
                    class="sc-unit"
                    v-html="`${product.quantity} x ${formatPrice(product.unit_price)}`"
                ></div>
            </div>

            <div class="c-recipe-product__cart">
                <button
                    v-if="!isOutOfStock"
                    class="c-btn c-btn--cart products__buy"
                    :class="{ 'is-loading': isAddingToCart }"
                    :disabled="isAddingAllToCart"
                    type="submit"
                    @click.prevent="addToCart($event)"
                >
                    <span>{{ $t('RecipeProducts.addToCart') }}</span>
                </button>

                <button
                    v-else
                    class="c-product-buy__cart c-btn c-btn--primary c-btn--watchdog js-watchdog-btn"
                    type="button"
                    :data-product-id="product.id"
                    data-toggle="modal"
                    data-target="#watchdog-modal"
                    :data-href="watchdogUrl"
                    :data-text-set="$t('RecipeProducts.notifyMeSet')"
                    :data-text-set-long="$t('RecipeProducts.notifyStockSet')"
                >
                    <i class="ico ico-account-notifications" aria-hidden="true"></i>
                    <span class="sc-long">{{ $t('RecipeProducts.notifyStock') }}</span>
                    <span class="sc-short">{{ $t('RecipeProducts.notifyMe') }}</span>
                </button>
            </div>
        </div>

        <div class="c-recipe-product__alternatives" v-if="alternatives.length && hasAlternativesOpen" ref="alternatives">
            <div class="c-recipe-alternatives">
                <div class="c-recipe-alternatives__items">
                    <div
                        class="c-recipe-alternatives__item"
                        v-for="alternative in filteredAlternatives"
                        :key="alternative.id"
                    >
                        <button
                            class="c-recipe-alternative"
                            :class="{'is-selected': isAlternativeSelected(alternative.id)}"
                            :disabled="isAddingAllToCart"
                            @click="selectAlternative(alternative)"
                        >
                            <div class="c-recipe-alternative__tick" v-if="isAlternativeSelected(alternative.id)">
                                <i class="ico ico-tick"></i>
                            </div>

                            <div class="c-recipe-alternative__image">
                                <img
                                    class="c-img"
                                    :src="thumbnail(alternative.image, 'sm')"
                                    :alt="alternative.name"
                                />
                            </div>

                            <div class="c-recipe-alternative__content">
                                <div class="c-recipe-alternative__name">
                                    {{ alternative.name }}
                                </div>

                                <div class="c-recipe-alternative__price">
                                    <span class="sc-actual" :class="{'has-action': alternative.is_action}" v-html="formatPrice(alternative.final_price)"></span>
                                    <!-- <span class="sc-old" v-if="alternative.final_price < alternative.old_price" v-html="formatPrice(alternative.old_price)"></span> -->
                                </div>

                                <div class="c-recipe-alternative__unit" v-html="`${alternative.quantity} x ${formatPrice(alternative.unit_price)}`">
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                <div class="c-recipe-alternatives__more" v-if="hasMoreAlternativesShown || moreAlternatives.length > 0">
                    <button class="c-btn c-btn--ghost" @click="toggleMoreAlternatives()">
                        <template v-if="hasMoreAlternativesShown">
                            <span>{{ $t('RecipeProducts.showLessAlternatives') }}</span>
                        </template>

                        <template v-else>
                            <span>{{ $t('RecipeProducts.showMoreAlternatives') }}</span>
                            <span class="c-badge c-badge--light-grey">{{ moreAlternatives.length }}</span>
                        </template>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import client from 'FrontendJsAssets/common/client'
import { formatPrice } from 'FrontendJsAssets/common/format'
import { thumbnail } from 'Functions/thumbnail'
import { checkResolution } from 'Functions/helpers'

export default {
    props: {
        ingredient: {
            type: Object,
            required: true
        },
        selectedAlternative: {
            type: Object,
            required: true
        },
        isAddingAllToCart: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            isAddingToCart: false,
            hasAlternativesOpen: false,
            moreAlternativesToShow: null,
            hasMoreAlternativesShown: false,
        }
    },
    beforeMount() {
        this.setMoreAlternativesVisiblity()

        rEvent.on('Window.resized', () => {
            this.setMoreAlternativesVisiblity()
        })
    },
    computed: {
        product() {
            let product = null

            if (this.selectedAlternative) {
                product = this.alternatives.find((alternative) => alternative.id === this.selectedAlternative.id)
            }

            return product || this.ingredient.product
        },
        alternatives() {
            return this.ingredient.alternatives
        },
        filteredAlternatives() {
            let alternatives = [...[this.ingredient.product], ...this.alternatives]

            if (this.hasMoreAlternativesShown) {
                return alternatives
            }

            return alternatives.slice(0, this.moreAlternativesToShow)
        },
        moreAlternatives() {
            if (this.hasMoreAlternativesShown) {
                return []
            }

            return this.alternatives.slice(this.moreAlternativesToShow - 1)
        },
        amount() {
            if (this.ingredient.amount_text) return this.ingredient.amount_text

            const amount = this.ingredient.amount
            const amountNumber = parseFloat(amount)

            if (!isNaN(amountNumber) && !Number.isInteger(amountNumber)) {
                return Math.round(amountNumber * 100) / 100
            }

            return amount
        },
        isOutOfStock() {
            return !!this.product.is_out_of_stock
        },
        watchdogUrl() {
            return window.Eshop.options.baseUrl + 'custom-modal-watchdog-availability/' + this.product.id
        },
    },
    methods: {
        formatPrice: formatPrice,
        thumbnail: thumbnail,
        setMoreAlternativesVisiblity() {
            if (checkResolution('cxs;xs')) {
                this.moreAlternativesToShow = 4
            } else {
                this.moreAlternativesToShow = 3
            }
        },
        toggleAlternatives() {
            const contentRects = this.$refs.content.getBoundingClientRect()
            this.hasAlternativesOpen = !this.hasAlternativesOpen

            if (!this.hasAlternativesOpen && contentRects.top < 0) {
                this.$nextTick(() => {
                    window.scroll({
                        top: window.scrollY + contentRects.top,
                        behavior: 'instant'
                    })
                })
            }
        },
        toggleMoreAlternatives() {
            this.hasMoreAlternativesShown = !this.hasMoreAlternativesShown
        },
        isAlternativeSelected(alternativeId) {
            if (!this.selectedAlternative) {
                return this.product.id === alternativeId
            }

            return this.selectedAlternative.id === alternativeId
        },
        selectAlternative(alternative) {
            this.$emit('selectAlternative', this.ingredient.id, alternative)
            this.toggleAlternatives()
        },
        addToCart($event) {
            if (this.isAddingToCart) {
                return
            }

            const button = $event.currentTarget
            this.isAddingToCart = true

            client.post(window.Eshop.options.baseUrl + 'cart-products/add', {
                    product_id: this.product.id,
                    quantity: this.product.quantity
                })
                .then((response) => {
                    rEvent.dispatch('Cart.addedProduct', {
                        response: response.data
                    })

                    window.Eshop.tippyAlert(button, response.data.message, {
                        theme: response.data.status ? 'success' : 'error',
                        zIndex: '200'
                    }, response.data.status ? 2500 : 5000)
                })
                .catch((error) => {
                    console.error(error)

                    window.Eshop.tippyAlert(button, this.$t("RecipeProducts.addToCartError"), {
                        theme: 'error',
                        zIndex: '200'
                    }, 5000)
                })
                .then(() => {
                    this.isAddingToCart = false
                })
        },
    },
}
</script>
